// GLSL source for BookOneWave — verbatim from ElevenLabs' docs hero shader,
// extended with a 16-band arc array for custom curves (arc slider) and a
// speech-envelope amplitude driver (talking mode, handled in JS).
//
// Don't edit these unless you know what you're doing. The `pnoise` helper is
// Stefan Gustavson's classic periodic Perlin noise, period 10.

export const vertexShader = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289((x * 34.0 + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
vec3 fade(vec3 t) { return t * t * t * (t * (t * 6.0 - 15.0) + 10.0); }

float pnoise(vec3 P, vec3 rep) {
  vec3 Pi0 = mod(floor(P), rep);
  vec3 Pi1 = mod(Pi0 + vec3(1.0), rep);
  Pi0 = mod289(Pi0); Pi1 = mod289(Pi1);
  vec3 Pf0 = fract(P); vec3 Pf1 = Pf0 - vec3(1.0);
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz; vec4 iz1 = Pi1.zzzz;
  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);
  vec4 gx0 = ixy0 * (1.0 / 7.0);
  vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);
  vec4 gx1 = ixy1 * (1.0 / 7.0);
  vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);
  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
  vec4 norm0 = taylorInvSqrt(vec4(dot(g000,g000), dot(g010,g010), dot(g100,g100), dot(g110,g110)));
  g000 *= norm0.x; g010 *= norm0.y; g100 *= norm0.z; g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001,g001), dot(g011,g011), dot(g101,g101), dot(g111,g111)));
  g001 *= norm1.x; g011 *= norm1.y; g101 *= norm1.z; g111 *= norm1.w;
  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);
  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000,n100,n010,n110), vec4(n001,n101,n011,n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  return 2.2 * mix(n_yz.x, n_yz.y, fade_xyz.x);
}

uniform float uTime;
uniform float uSpeed;
uniform float uAmplitude;
uniform float uDensity;
uniform float uArcPeak;
uniform float uViewHalf;
uniform float uArcs[16];
varying vec2 vUv;

float sampleArcs(float t) {
  float idx = clamp(t, 0.0, 0.9999) * 15.0;
  int i0 = int(floor(idx));
  int i1 = min(i0 + 1, 15);
  float f = idx - float(i0);
  float a0, a1;
  #define GRAB(k) if (i0 == k) a0 = uArcs[k]; if (i1 == k) a1 = uArcs[k];
  GRAB(0)  GRAB(1)  GRAB(2)  GRAB(3)
  GRAB(4)  GRAB(5)  GRAB(6)  GRAB(7)
  GRAB(8)  GRAB(9)  GRAB(10) GRAB(11)
  GRAB(12) GRAB(13) GRAB(14) GRAB(15)
  #undef GRAB
  return mix(a0, a1, f);
}

void main() {
  vUv = uv;
  float displacement = uAmplitude * pnoise(position * uDensity + uTime * uSpeed, vec3(10.0));
  vec3 newPosition = position + normal * displacement;

  float t = clamp((position.x / uViewHalf + 1.0) * 0.5, 0.0, 1.0);
  float bandVal = sampleArcs(t);
  newPosition.z -= bandVal * uArcPeak;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
}
`;

export const fragmentShader = /* glsl */ `
struct GradientStop { vec4 color; float position; };
#define NUM_GRADIENT_STOPS (7)
#define TEXTURE_NOISE_OPACITY (0.006)

varying vec2 vUv;
uniform float uGradientRepeat;
uniform GradientStop uGradientStops[NUM_GRADIENT_STOPS];

void setGradientColor(GradientStop colors[NUM_GRADIENT_STOPS], float factor, out vec4 finalColor) {
  int index = 0;
  float repeatedFactor = fract(factor * uGradientRepeat);
  for (int i = 0; i < NUM_GRADIENT_STOPS - 1; i++) {
    GradientStop c = colors[i];
    if (c.position <= repeatedFactor) { index = i; }
  }
  GradientStop c = colors[index];
  GradientStop n = colors[index + 1];
  float range = n.position - c.position;
  float lf = smoothstep(0.0, 1.0, (repeatedFactor - c.position) / range);
  finalColor.rgb = mix(c.color.rgb, n.color.rgb, lf);
  finalColor.a   = mix(c.color.a,   n.color.a,   lf);
}

float random(vec2 p) {
  vec2 K1 = vec2(23.14069263277926, 2.665144142690225);
  return fract(cos(dot(p, K1)) * 12345.6789);
}

void main() {
  vec4 finalColor;
  setGradientColor(uGradientStops, vUv.y, finalColor);
  finalColor.rgb += random(gl_FragCoord.xy) * TEXTURE_NOISE_OPACITY;
  gl_FragColor = finalColor;
}
`;
